<template>
  <div class="incidents-container">
    
    <!-- Hero Section -->
    <section class="incidents-hero">
      <div class="hero-content">
        <div class="hero-badge">
          <v-icon icon="mdi-shield-check" size="20"></v-icon>
          <span>{{ $t('incidents.hero.badge') }}</span>
        </div>
        <h1 class="hero-title">{{ $t('incidents.hero.title') }}</h1>
        <p class="hero-description">{{ $t('incidents.hero.description') }}</p>
        
        <div class="hero-stats">
          <div class="hero-stat">
            <v-icon icon="mdi-clock-fast" size="32" color="primary"></v-icon>
            <div class="stat-info">
              <div class="stat-value">24/7</div>
              <div class="stat-label">{{ $t('incidents.hero.stats.availability') }}</div>
            </div>
          </div>
          <div class="hero-stat">
            <v-icon icon="mdi-account-group" size="32" color="success"></v-icon>
            <div class="stat-info">
              <div class="stat-value">50+</div>
              <div class="stat-label">{{ $t('incidents.hero.stats.providers') }}</div>
            </div>
          </div>
          <div class="hero-stat">
            <v-icon icon="mdi-speedometer" size="32" color="warning"></v-icon>
            <div class="stat-info">
              <div class="stat-value">&lt; 2h</div>
              <div class="stat-label">{{ $t('incidents.hero.stats.response') }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works -->
    <section class="how-it-works">
      <div class="section-header">
        <v-icon icon="mdi-information" size="40" color="primary"></v-icon>
        <h2 class="section-title">{{ $t('incidents.howItWorks.title') }}</h2>
        <p class="section-subtitle">{{ $t('incidents.howItWorks.subtitle') }}</p>
      </div>

      <div class="steps-grid">
        <div class="step-card" v-for="(step, index) in steps" :key="index">
          <div class="step-number">{{ index + 1 }}</div>
          <div class="step-icon">
            <v-icon :icon="step.icon" size="48"></v-icon>
          </div>
          <h3 class="step-title">{{ $t(step.title) }}</h3>
          <p class="step-description">{{ $t(step.description) }}</p>
        </div>
      </div>
    </section>

    <!-- Categories Overview -->
    <section class="categories-section">
      <div class="section-header">
        <v-icon icon="mdi-shape" size="40" color="primary"></v-icon>
        <h2 class="section-title">{{ $t('incidents.categories.title') }}</h2>
        <p class="section-subtitle">{{ $t('incidents.categories.subtitle') }}</p>
      </div>

      <div class="categories-grid">
        <v-card 
          v-for="category in categories" 
          :key="category.key"
          class="category-card"
          elevation="2"
        >
          <v-card-text>
            <div class="category-icon-wrapper" :style="{ background: category.color }">
              <v-icon :icon="category.icon" size="40" color="white"></v-icon>
            </div>
            <h3 class="category-name">{{ $t(category.name) }}</h3>
            <p class="category-description">{{ $t(category.description) }}</p>
            
            <div class="providers-section">
              <div class="providers-label">
                <v-icon icon="mdi-account-wrench" size="16"></v-icon>
                {{ $t('incidents.categories.providers') }}:
              </div>
              <div class="providers-list">
                <v-chip 
                  v-for="provider in category.providers" 
                  :key="provider"
                  size="small"
                  variant="outlined"
                  class="provider-chip"
                >
                  {{ provider }}
                </v-chip>
              </div>
            </div>

            <div class="response-time">
              <v-icon icon="mdi-timer-outline" size="16" color="success"></v-icon>
              <span>{{ $t('incidents.categories.avgResponse') }}: {{ category.responseTime }}</span>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </section>

    <!-- Priority Levels -->
    <section class="priority-section">
      <div class="section-header">
        <v-icon icon="mdi-flag-variant" size="40" color="primary"></v-icon>
        <h2 class="section-title">{{ $t('incidents.priorities.title') }}</h2>
        <p class="section-subtitle">{{ $t('incidents.priorities.subtitle') }}</p>
      </div>

      <div class="priority-grid">
        <v-card 
          v-for="priority in priorities" 
          :key="priority.key"
          class="priority-card"
          :class="`priority-${priority.key}`"
        >
          <v-card-text>
            <div class="priority-header">
              <v-icon :icon="priority.icon" size="32" :color="priority.color"></v-icon>
              <h3 class="priority-name">{{ $t(priority.name) }}</h3>
            </div>
            <p class="priority-description">{{ $t(priority.description) }}</p>
            
            <div class="priority-details">
              <div class="detail-item">
                <v-icon icon="mdi-clock-alert" size="18"></v-icon>
                <span>{{ $t('incidents.priorities.responseTime') }}: <strong>{{ priority.responseTime }}</strong></span>
              </div>
              <div class="detail-item">
                <v-icon icon="mdi-account-clock" size="18"></v-icon>
                <span>{{ $t('incidents.priorities.resolution') }}: <strong>{{ priority.resolutionTime }}</strong></span>
              </div>
            </div>

            <div class="priority-examples">
              <div class="examples-label">{{ $t('incidents.priorities.examples') }}:</div>
              <ul class="examples-list">
                <li v-for="example in priority.examples" :key="example">{{ $t(example) }}</li>
              </ul>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </section>

    <!-- Map Showcase -->
    <section class="map-showcase">
      <div class="section-header">
        <v-icon icon="mdi-map-marker-radius" size="40" color="primary"></v-icon>
        <h2 class="section-title">{{ $t('incidents.mapShowcase.title') }}</h2>
        <p class="section-subtitle">{{ $t('incidents.mapShowcase.subtitle') }}</p>
      </div>

      <v-card class="map-card" elevation="8">
        <v-card-text>
          <div class="map-container">
            <div id="showcase-map" class="showcase-map"></div>
            <div class="map-overlay">
              <v-card class="overlay-card">
                <v-card-text>
                  <div class="overlay-header">
                    <v-icon icon="mdi-information" color="primary"></v-icon>
                    <span>{{ $t('incidents.mapShowcase.feature') }}</span>
                  </div>
                  <ul class="feature-list">
                    <li>
                      <v-icon icon="mdi-check-circle" color="success" size="18"></v-icon>
                      {{ $t('incidents.mapShowcase.features.pinpoint') }}
                    </li>
                    <li>
                      <v-icon icon="mdi-check-circle" color="success" size="18"></v-icon>
                      {{ $t('incidents.mapShowcase.features.realtime') }}
                    </li>
                    <li>
                      <v-icon icon="mdi-check-circle" color="success" size="18"></v-icon>
                      {{ $t('incidents.mapShowcase.features.tracking') }}
                    </li>
                    <li>
                      <v-icon icon="mdi-check-circle" color="success" size="18"></v-icon>
                      {{ $t('incidents.mapShowcase.features.history') }}
                    </li>
                  </ul>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </section>

    <!-- Example Incidents -->
    <section class="examples-section">
      <div class="section-header">
        <v-icon icon="mdi-file-document-multiple" size="40" color="primary"></v-icon>
        <h2 class="section-title">{{ $t('incidents.examples.title') }}</h2>
        <p class="section-subtitle">{{ $t('incidents.examples.subtitle') }}</p>
      </div>

      <div class="examples-grid">
        <v-card 
          v-for="example in exampleIncidents" 
          :key="example.id"
          class="example-card"
          elevation="2"
        >
          <div class="example-header" :style="{ background: example.headerColor }">
            <div class="example-id">{{ example.id }}</div>
            <v-chip :color="example.statusColor" size="small" variant="flat">
              {{ $t(example.status) }}
            </v-chip>
          </div>
          <v-card-text>
            <div class="example-meta">
              <v-chip :color="example.priorityColor" size="small" variant="outlined">
                <v-icon :icon="example.priorityIcon" start size="16"></v-icon>
                {{ $t(example.priority) }}
              </v-chip>
              <div class="example-category">
                <v-icon :icon="example.categoryIcon" size="18"></v-icon>
                {{ $t(example.category) }}
              </div>
            </div>

            <h3 class="example-title">{{ $t(example.title) }}</h3>
            <p class="example-description">{{ $t(example.description) }}</p>

            <div class="example-details">
              <div class="detail-row">
                <v-icon icon="mdi-account-wrench" size="18" color="primary"></v-icon>
                <span><strong>{{ $t('incidents.examples.provider') }}:</strong> {{ example.provider }}</span>
              </div>
              <div class="detail-row">
                <v-icon icon="mdi-clock-outline" size="18" color="success"></v-icon>
                <span><strong>{{ $t('incidents.examples.reported') }}:</strong> {{ example.reportedTime }}</span>
              </div>
              <div class="detail-row">
                <v-icon icon="mdi-map-marker" size="18" color="error"></v-icon>
                <span><strong>{{ $t('incidents.examples.location') }}:</strong> {{ $t(example.location) }}</span>
              </div>
            </div>

            <v-divider class="my-3"></v-divider>

            <div class="timeline-info">
              <div class="timeline-label">{{ $t('incidents.examples.timeline') }}:</div>
              <div class="timeline-steps">
                <div v-for="step in example.timeline" :key="step.time" class="timeline-step">
                  <v-icon :icon="step.icon" size="16" :color="step.color"></v-icon>
                  <span class="timeline-time">{{ step.time }}</span>
                  <span class="timeline-text">{{ $t(step.text) }}</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </div>
    </section>

    <!-- Benefits Section -->
    <section class="benefits-section">
      <div class="section-header">
        <v-icon icon="mdi-star-circle" size="40" color="primary"></v-icon>
        <h2 class="section-title">{{ $t('incidents.benefits.title') }}</h2>
        <p class="section-subtitle">{{ $t('incidents.benefits.subtitle') }}</p>
      </div>

      <div class="benefits-grid">
        <v-card 
          v-for="benefit in benefits" 
          :key="benefit.title"
          class="benefit-card"
        >
          <v-card-text>
            <div class="benefit-icon" :style="{ background: benefit.color }">
              <v-icon :icon="benefit.icon" size="36" color="white"></v-icon>
            </div>
            <h3 class="benefit-title">{{ $t(benefit.title) }}</h3>
            <p class="benefit-description">{{ $t(benefit.description) }}</p>
          </v-card-text>
        </v-card>
      </div>
    </section>

   

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const { t } = useI18n();

const steps = [
  {
    icon: 'mdi-file-document-plus',
    title: 'incidents.howItWorks.steps.report.title',
    description: 'incidents.howItWorks.steps.report.description'
  },
  {
    icon: 'mdi-map-marker-plus',
    title: 'incidents.howItWorks.steps.locate.title',
    description: 'incidents.howItWorks.steps.locate.description'
  },
  {
    icon: 'mdi-account-search',
    title: 'incidents.howItWorks.steps.assign.title',
    description: 'incidents.howItWorks.steps.assign.description'
  },
  {
    icon: 'mdi-progress-check',
    title: 'incidents.howItWorks.steps.track.title',
    description: 'incidents.howItWorks.steps.track.description'
  }
];

const categories = [
  {
    key: 'electrical',
    icon: 'mdi-lightning-bolt',
    color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    name: 'incidents.categories.electrical.name',
    description: 'incidents.categories.electrical.description',
    providers: ['ElectroTech Solutions', 'PowerPro Services', 'Voltage Experts'],
    responseTime: '30 min'
  },
  {
    key: 'it',
    icon: 'mdi-laptop',
    color: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    name: 'incidents.categories.it.name',
    description: 'incidents.categories.it.description',
    providers: ['IT Support Team', 'TechFix Pro', 'Digital Solutions'],
    responseTime: '15 min'
  },
  {
    key: 'locks',
    icon: 'mdi-lock',
    color: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    name: 'incidents.categories.locks.name',
    description: 'incidents.categories.locks.description',
    providers: ['SecureLock Systems', 'KeyMaster Services', 'Access Control Pro'],
    responseTime: '20 min'
  },
  {
    key: 'pantry',
    icon: 'mdi-coffee',
    color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    name: 'incidents.categories.pantry.name',
    description: 'incidents.categories.pantry.description',
    providers: ['Pantry Management', 'Food Services', 'Kitchen Maintenance'],
    responseTime: '45 min'
  },
  {
    key: 'hvac',
    icon: 'mdi-air-conditioner',
    color: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
    name: 'incidents.categories.hvac.name',
    description: 'incidents.categories.hvac.description',
    providers: ['Climate Control', 'HVAC Experts', 'Air Systems'],
    responseTime: '1 hour'
  },
  {
    key: 'plumbing',
    icon: 'mdi-pipe',
    color: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
    name: 'incidents.categories.plumbing.name',
    description: 'incidents.categories.plumbing.description',
    providers: ['PlumbWorks', 'Pipe Masters', 'Flow Solutions'],
    responseTime: '25 min'
  }
];

const priorities = [
  {
    key: 'low',
    icon: 'mdi-flag-outline',
    color: 'success',
    name: 'incidents.priorities.low.name',
    description: 'incidents.priorities.low.description',
    responseTime: '4-8 hours',
    resolutionTime: '24-48 hours',
    examples: [
      'incidents.priorities.low.example1',
      'incidents.priorities.low.example2',
      'incidents.priorities.low.example3'
    ]
  },
  {
    key: 'medium',
    icon: 'mdi-flag',
    color: 'warning',
    name: 'incidents.priorities.medium.name',
    description: 'incidents.priorities.medium.description',
    responseTime: '2-4 hours',
    resolutionTime: '8-24 hours',
    examples: [
      'incidents.priorities.medium.example1',
      'incidents.priorities.medium.example2',
      'incidents.priorities.medium.example3'
    ]
  },
  {
    key: 'high',
    icon: 'mdi-flag',
    color: 'error',
    name: 'incidents.priorities.high.name',
    description: 'incidents.priorities.high.description',
    responseTime: '30-60 min',
    resolutionTime: '2-8 hours',
    examples: [
      'incidents.priorities.high.example1',
      'incidents.priorities.high.example2',
      'incidents.priorities.high.example3'
    ]
  },
  {
    key: 'critical',
    icon: 'mdi-alert-octagon',
    color: 'purple',
    name: 'incidents.priorities.critical.name',
    description: 'incidents.priorities.critical.description',
    responseTime: '< 15 min',
    resolutionTime: '< 2 hours',
    examples: [
      'incidents.priorities.critical.example1',
      'incidents.priorities.critical.example2',
      'incidents.priorities.critical.example3'
    ]
  }
];

const exampleIncidents = [
  {
    id: 'INC-2847',
    headerColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    status: 'incidents.examples.incident1.status',
    statusColor: 'success',
    priority: 'incidents.examples.incident1.priority',
    priorityColor: 'error',
    priorityIcon: 'mdi-flag',
    category: 'incidents.examples.incident1.category',
    categoryIcon: 'mdi-lightning-bolt',
    title: 'incidents.examples.incident1.title',
    description: 'incidents.examples.incident1.description',
    provider: 'ElectroTech Solutions',
    reportedTime: '2 hours ago',
    location: 'incidents.examples.incident1.location',
    timeline: [
      { time: '14:30', icon: 'mdi-file-document-plus', color: 'primary', text: 'incidents.examples.timeline.reported' },
      { time: '14:45', icon: 'mdi-account-check', color: 'info', text: 'incidents.examples.timeline.assigned' },
      { time: '15:00', icon: 'mdi-tools', color: 'warning', text: 'incidents.examples.timeline.started' },
      { time: '16:15', icon: 'mdi-check-circle', color: 'success', text: 'incidents.examples.timeline.resolved' }
    ]
  },
  {
    id: 'INC-2848',
    headerColor: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    status: 'incidents.examples.incident2.status',
    statusColor: 'info',
    priority: 'incidents.examples.incident2.priority',
    priorityColor: 'purple',
    priorityIcon: 'mdi-alert-octagon',
    category: 'incidents.examples.incident2.category',
    categoryIcon: 'mdi-laptop',
    title: 'incidents.examples.incident2.title',
    description: 'incidents.examples.incident2.description',
    provider: 'IT Support Team',
    reportedTime: '30 min ago',
    location: 'incidents.examples.incident2.location',
    timeline: [
      { time: '15:45', icon: 'mdi-file-document-plus', color: 'primary', text: 'incidents.examples.timeline.reported' },
      { time: '15:50', icon: 'mdi-account-check', color: 'info', text: 'incidents.examples.timeline.assigned' },
      { time: '16:00', icon: 'mdi-tools', color: 'warning', text: 'incidents.examples.timeline.started' }
    ]
  },
  {
    id: 'INC-2849',
    headerColor: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    status: 'incidents.examples.incident3.status',
    statusColor: 'warning',
    priority: 'incidents.examples.incident3.priority',
    priorityColor: 'warning',
    priorityIcon: 'mdi-flag',
    category: 'incidents.examples.incident3.category',
    categoryIcon: 'mdi-pipe',
    title: 'incidents.examples.incident3.title',
    description: 'incidents.examples.incident3.description',
    provider: 'PlumbWorks',
    reportedTime: '1 hour ago',
    location: 'incidents.examples.incident3.location',
    timeline: [
      { time: '15:15', icon: 'mdi-file-document-plus', color: 'primary', text: 'incidents.examples.timeline.reported' },
      { time: '15:30', icon: 'mdi-account-check', color: 'info', text: 'incidents.examples.timeline.assigned' }
    ]
  }
];

const benefits = [
  {
    icon: 'mdi-speedometer',
    color: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    title: 'incidents.benefits.fast.title',
    description: 'incidents.benefits.fast.description'
  },
  {
    icon: 'mdi-eye',
    color: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    title: 'incidents.benefits.transparent.title',
    description: 'incidents.benefits.transparent.description'
  },
  {
    icon: 'mdi-shield-check',
    color: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    title: 'incidents.benefits.reliable.title',
    description: 'incidents.benefits.reliable.description'
  },
  {
    icon: 'mdi-chart-line',
    color: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    title: 'incidents.benefits.analytics.title',
    description: 'incidents.benefits.analytics.description'
  }
];

onMounted(() => {
  // Initialize map with multiple markers for showcase
  const map = L.map('showcase-map').setView([48.1351, 11.5820], 14);

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors'
  }).addTo(map);

  // Add multiple example incident markers
  const incidents = [
    { lat: 48.1351, lng: 11.5820, title: 'Electrical Issue', color: 'red' },
    { lat: 48.1365, lng: 11.5835, title: 'IT Support', color: 'blue' },
    { lat: 48.1340, lng: 11.5810, title: 'Plumbing', color: 'green' },
    { lat: 48.1358, lng: 11.5828, title: 'HVAC', color: 'orange' }
  ];

  incidents.forEach(incident => {
    const marker = L.marker([incident.lat, incident.lng], {
      icon: L.divIcon({
        className: 'custom-marker',
        html: `<div style="background: ${incident.color}; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>`,
        iconSize: [30, 30]
      })
    }).addTo(map);

    marker.bindPopup(`<b>${incident.title}</b><br>Click to view details`);
  });
});
</script>

<style scoped>
.incidents-container {
  min-height: 100vh;
  min-height: 100dvh;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

/* Hero Section */
.incidents-hero {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 4rem 2rem;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.incidents-hero::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: rotate 20s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.5rem;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 800;
  margin: 0 0 1.5rem 0;
  line-height: 1.2;
}

.hero-description {
  font-size: 1.25rem;
  max-width: 800px;
  margin: 0 auto 3rem;
  opacity: 0.95;
  line-height: 1.6;
}

.hero-stats {
  display: flex;
  justify-content: center;
  gap: 3rem;
  flex-wrap: wrap;
}

.hero-stat {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 1.5rem 2rem;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
}

.stat-label {
  font-size: 0.875rem;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Section Header */
.section-header {
  text-align: center;
  margin-bottom: 3rem;
  padding: 2rem;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #1a202c;
  margin: 1rem 0;
}

.section-subtitle {
  font-size: 1.125rem;
  color: #718096;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
}

/* How It Works */
.how-it-works {
  padding: 4rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.step-card {
  text-align: center;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  transition: transform 0.3s;
}

.step-card:hover {
  transform: translateY(-8px);
}

.step-number {
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.25rem;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.step-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem auto;
  color: #667eea;
}

.step-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin: 1rem 0;
}

.step-description {
  font-size: 0.95rem;
  color: #718096;
  line-height: 1.6;
  margin: 0;
}

/* Categories Section */
.categories-section {
  padding: 4rem 2rem;
  background: white;
}

.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.category-card {
  transition: transform 0.3s, box-shadow 0.3s;
}

.category-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.category-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.category-name {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 1rem 0;
}

.category-description {
  font-size: 0.95rem;
  color: #718096;
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
}

.providers-section {
  margin: 1.5rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
}

.providers-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 0.75rem;
}

.providers-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.provider-chip {
  font-size: 0.75rem;
}

.response-time {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #2e7d32;
  font-weight: 600;
  margin-top: 1rem;
}

/* Priority Section */
.priority-section {
  padding: 4rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.priority-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
}

.priority-card {
  border-left: 4px solid;
  transition: transform 0.3s;
}

.priority-card:hover {
  transform: translateY(-8px);
}

.priority-low {
  border-left-color: #4caf50;
}

.priority-medium {
  border-left-color: #ff9800;
}

.priority-high {
  border-left-color: #f44336;
}

.priority-critical {
  border-left-color: #9c27b0;
}

.priority-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.priority-name {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

.priority-description {
  font-size: 0.95rem;
  color: #718096;
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
}

.priority-details {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #4a5568;
  margin-bottom: 0.5rem;
}

.detail-item:last-child {
  margin-bottom: 0;
}

.priority-examples {
  margin-top: 1.5rem;
}

.examples-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.examples-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.examples-list li {
  font-size: 0.875rem;
  color: #718096;
  padding: 0.5rem 0;
  padding-left: 1.5rem;
  position: relative;
}

.examples-list li::before {
  content: '•';
  position: absolute;
  left: 0.5rem;
  color: #667eea;
  font-weight: 700;
}

/* Map Showcase */
.map-showcase {
  padding: 4rem 2rem;
  background: white;
}

.map-card {
  max-width: 1400px;
  margin: 0 auto;
}

.map-container {
  position: relative;
}

.showcase-map {
  height: 500px;
  width: 100%;
  border-radius: 12px;
}

.map-overlay {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 10;
}

.overlay-card {
  min-width: 280px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.overlay-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 1rem;
  font-size: 1.1rem;
}

.feature-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.feature-list li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  font-size: 0.9rem;
  color: #4a5568;
}

/* Examples Section */
.examples-section {
  padding: 4rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.examples-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
}

.example-card {
  transition: transform 0.3s, box-shadow 0.3s;
}

.example-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.example-header {
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
}

.example-id {
  font-family: monospace;
  font-size: 1.25rem;
  font-weight: 700;
}

.example-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.example-category {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #718096;
}

.example-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 1rem 0;
}

.example-description {
  font-size: 0.95rem;
  color: #718096;
  line-height: 1.6;
  margin: 0 0 1.5rem 0;
}

.example-details {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 12px;
  margin-bottom: 1rem;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  color: #4a5568;
  margin-bottom: 0.75rem;
}

.detail-row:last-child {
  margin-bottom: 0;
}

.timeline-info {
  margin-top: 1.5rem;
}

.timeline-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.timeline-steps {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.timeline-step {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  padding-left: 1rem;
  border-left: 2px solid #e9ecef;
}

.timeline-time {
  font-weight: 600;
  color: #667eea;
  min-width: 50px;
}

.timeline-text {
  color: #718096;
}

/* Benefits Section */
.benefits-section {
  padding: 4rem 2rem;
  background: white;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.benefit-card {
  text-align: center;
  padding: 2rem;
  transition: transform 0.3s;
}

.benefit-card:hover {
  transform: translateY(-8px);
}

.benefit-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
}

.benefit-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 1rem 0;
}

.benefit-description {
  font-size: 0.95rem;
  color: #718096;
  line-height: 1.6;
  margin: 0;
}

/* CTA Section */
.cta-section {
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.cta-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 24px;
}

.cta-content {
  text-align: center;
  padding: 3rem;
}

.cta-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 1.5rem 0;
}

.cta-description {
  font-size: 1.125rem;
  max-width: 600px;
  margin: 0 auto 2rem;
  opacity: 0.95;
}

/* Responsive */
@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }

  .hero-description {
    font-size: 1rem;
  }

  .hero-stats {
    flex-direction: column;
    gap: 1rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .steps-grid,
  .categories-grid,
  .priority-grid,
  .examples-grid,
  .benefits-grid {
    grid-template-columns: 1fr;
  }

  .map-overlay {
    position: static;
    margin-top: 1rem;
  }

  .showcase-map {
    height: 300px;
  }

  .cta-title {
    font-size: 1.75rem;
  }
}
</style>